import { useApi } from "./useApi";
import { useQuery, useMutation } from "@tanstack/react-query";
import { downloadFile } from "@utils";

/**
 * MS-573 — Host freemium wallet («المحفظة الإلكترونية»).
 *
 * Display-only: every money figure is rendered exactly as the backend returns it.
 * This hook NEVER adds, subtracts, or multiplies an amount — `netDue`, `pending`,
 * and each commission-row `amount` come pre-computed from MsoolV2_Accounting.
 * Host-only surface: it must never carry guest VAT or guest totals.
 *
 * All endpoints are [OwnerOnly] backend-side; a team-member token returns 403 with
 * errorMessages[0].code 1039/1040, which the container branches on.
 */

/** Mirrors WalletPeriod on the backend (PeriodModels.cs). SinceLastTransfer safely
 *  resolves to ThisMonth server-side; Custom needs a from/to range. */
export enum WalletPeriod {
  SinceLastTransfer = 1,
  ThisMonth = 2,
  LastMonth = 3,
  ThisQuarter = 4,
  LastQuarter = 5,
  Custom = 6,
}

export interface BankAccountSummary {
  holderName: string;
  bankName: string;
  ibanLast4: string;
}

export interface WalletSummary {
  totalBookings: number;
  msoolCommission: number;
  netDue: number;
  /** B2 (MS-573) — «قيد الانتظار»: net-due of not-yet-checked-in bookings. Absent
   *  until Motasem deploys the additive field; rendered only when present. */
  pending?: number | null;
  nextTransferDate?: string | null;
  bankAccount?: BankAccountSummary | null;
  window: { fromInclusive: string; toExclusive: string };
  resolvedPeriod: WalletPeriod;
}

/** GET BankAccount — never carries the full IBAN, only its last four digits. */
export interface BankAccount {
  ibanLast4: string;
  holderName: string;
  bankName: string;
  isVatRegistered: boolean;
  taxNumber?: string | null;
}

export interface CommissionInvoiceRow {
  /** Null when the commission invoice has not been issued yet (MS-627). */
  id: number | null;
  invoiceNumber: string | null;
  /** The invoice date, or the payment date for a row not invoiced yet. */
  issuedAt: string;
  reservationId: string;
  amount: number;
  /** B3 (MS-573) — InvoiceType: 2 HostCommission, 4 HostCommissionExtension.
   *  Absent until deployed; the badge defaults to «فاتورة عمولة» meanwhile. */
  type?: number | null;
  /** B4 (MS-573) — the guest-facing «MSL-XXXXXX» reference. Nullable (pre-MS-541
   *  bookings / missing reservation); the column falls back to invoiceNumber, never
   *  reservationId (which embeds the host email). */
  bookingCode?: string | null;
  /** MS-627 — false for a booking Msool collected whose commission invoice is not issued
   *  yet: it counts in the wallet but has nothing to download. Absent means issued. */
  invoiceIssued?: boolean;
}

export interface SaveBankAccountBody {
  iban: string;
  holderName: string;
  bankName: string;
  isVatRegistered: boolean;
  taxNumber?: string | null;
}

interface PeriodParams {
  period: WalletPeriod;
  from?: string;
  to?: string;
}

const SUMMARY_KEY = "hostWallet/summary";
const COMMISSION_KEY = "hostWallet/commission";
const BANK_KEY = "hostWallet/bankAccount";

const useHostWallet = () => {
  const { apiPrivate } = useApi();

  const buildParams = ({ period, from, to }: PeriodParams) => {
    const params: Record<string, string | number> = { period };
    if (period === WalletPeriod.Custom) {
      if (from) params.from = from;
      if (to) params.to = to;
    }
    return params;
  };

  const getSummary = async (p: PeriodParams): Promise<WalletSummary> => {
    const { data } = await apiPrivate.get<{ result: WalletSummary }>(
      "/api/HostWallet/Summary",
      { params: buildParams(p) }
    );
    return data.result;
  };

  const getCommissionInvoices = async (
    p: PeriodParams
  ): Promise<{ rows: CommissionInvoiceRow[]; totalRecords: number }> => {
    const { data } = await apiPrivate.get<{
      result: CommissionInvoiceRow[];
      totalRecords: number;
    }>("/api/HostWallet/CommissionInvoices", { params: buildParams(p) });
    return { rows: data.result ?? [], totalRecords: data.totalRecords ?? 0 };
  };

  const getBankAccount = async (): Promise<BankAccount | null> => {
    const { data } = await apiPrivate.get<{ result: BankAccount | null }>(
      "/api/HostWallet/BankAccount"
    );
    return data.result ?? null;
  };

  const saveBankAccount = async (body: SaveBankAccountBody) => {
    const { data } = await apiPrivate.post("/api/HostWallet/SaveBankAccount", body);
    return data;
  };

  /** Streams the ORIGINALLY issued commission PDF (never regenerated / renumbered). */
  const downloadCommissionInvoice = async (invoiceId: number) => {
    const response = await apiPrivate.get(
      `/api/HostWallet/CommissionInvoiceDownload/${invoiceId}`,
      { responseType: "blob" }
    );
    downloadFile(response, `commission-invoice-${invoiceId}.pdf`);
  };

  /** Every commission document in the resolved window as one zip. 404 when empty. */
  const downloadAllCommissionInvoices = async (p: PeriodParams) => {
    const response = await apiPrivate.get(
      "/api/HostWallet/CommissionInvoicesDownloadAll",
      { params: buildParams(p), responseType: "blob" }
    );
    downloadFile(response, "commission-invoices.zip");
  };

  const WalletSummaryQuery = (p: PeriodParams) =>
    useQuery({
      queryKey: [SUMMARY_KEY, p.period, p.from ?? "", p.to ?? ""],
      queryFn: () => getSummary(p),
      retry: false,
    });

  const CommissionInvoicesQuery = (p: PeriodParams) =>
    useQuery({
      queryKey: [COMMISSION_KEY, p.period, p.from ?? "", p.to ?? ""],
      queryFn: () => getCommissionInvoices(p),
      retry: false,
    });

  const BankAccountQuery = () =>
    useQuery({
      queryKey: [BANK_KEY],
      queryFn: getBankAccount,
      retry: false,
    });

  const SaveBankAccountMutate = (callbacks?: {
    onSuccess?: (data: unknown) => void;
    onError?: (error: unknown) => void;
  }) =>
    useMutation({
      mutationFn: saveBankAccount,
      onSuccess: callbacks?.onSuccess,
      onError: callbacks?.onError,
    });

  return {
    WalletSummaryQuery,
    CommissionInvoicesQuery,
    BankAccountQuery,
    SaveBankAccountMutate,
    downloadCommissionInvoice,
    downloadAllCommissionInvoices,
    keys: { SUMMARY_KEY, COMMISSION_KEY, BANK_KEY },
  };
};

export { useHostWallet };
